/**
 * Mordeczka.eu Admin - Main App with Ant Design
 */

import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, App as AntApp } from "antd";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import {
  DashboardPage,
  BooksListPage,
  MoviesListPage,
  SeriesListPage,
  BookFormPage,
  MovieFormPage,
  SeriesFormPage,
  AddSeasonPage,
  LanguagesPage,
  NationalitiesPage,
  RolesPage,
  TagsPage,
  GenresPage,
  LiteraryGenresPage,
  StreamingPlatformsPage,
  ImageCategoriesPage,
  ImagesPage,
  AuthorsPage,
  PublishersPage,
  DirectorsPage,
  CinemasPage,
  UsersPage,
  MyLibraryDashboardPage,
  MyLibraryEntriesPage,
  MyLibraryAddPage,
  MyLibraryAddBookPage,
  MyLibraryAddMoviePage,
  MyLibraryAddSeriesSeasonPage,
  MyLibraryEditPage,
  PurchasesListPage,
  PurchaseFormPage,
  WatchBrandsPage,
  PerfumeBrandsPage,
  MyLibraryStatsPage,
  SubscriptionTypesPage,
  SystemPage,
} from "./pages";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#1677ff",
            borderRadius: 6,
            fontFamily:
              "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          },
        }}
      >
        <AntApp>
          <BrowserRouter
            future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
          >
            <Routes>
              {/* Public route - Login */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes - require authentication */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<MyLibraryDashboardPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                {/* Books */}
                <Route path="books" element={<BooksListPage />} />
                <Route path="books/new" element={<BookFormPage />} />
                <Route path="books/:id/edit" element={<BookFormPage />} />
                {/* Movies */}
                <Route path="movies" element={<MoviesListPage />} />
                <Route path="movies/new" element={<MovieFormPage />} />
                <Route path="movies/:id/edit" element={<MovieFormPage />} />
                {/* Series */}
                <Route path="series" element={<SeriesListPage />} />
                <Route path="series/new" element={<SeriesFormPage />} />
                <Route path="series/:id/edit" element={<SeriesFormPage />} />
                <Route
                  path="series/:id/add-season"
                  element={<AddSeasonPage />}
                />
                {/* Dictionaries */}
                <Route path="languages" element={<LanguagesPage />} />
                <Route path="nationalities" element={<NationalitiesPage />} />
                <Route path="tags" element={<TagsPage />} />
                <Route path="genres" element={<GenresPage />} />
                <Route
                  path="literary-genres"
                  element={<LiteraryGenresPage />}
                />
                <Route
                  path="streaming-platforms"
                  element={<StreamingPlatformsPage />}
                />
                <Route
                  path="image-categories"
                  element={<ImageCategoriesPage />}
                />
                <Route path="images" element={<ImagesPage />} />
                <Route path="authors" element={<AuthorsPage />} />
                <Route path="publishers" element={<PublishersPage />} />
                <Route path="directors" element={<DirectorsPage />} />
                <Route path="roles" element={<RolesPage />} />
                <Route path="cinemas" element={<CinemasPage />} />
                <Route
                  path="subscription-types"
                  element={<SubscriptionTypesPage />}
                />
                {/* Administration */}
                <Route path="users" element={<UsersPage />} />
                <Route
                  path="system"
                  element={<Navigate to="/system/status" replace />}
                />
                <Route path="system/status" element={<SystemPage />} />
                <Route path="system/traffic" element={<SystemPage />} />

                {/* My Library */}
                <Route path="my-library" element={<MyLibraryDashboardPage />} />
                <Route path="my-library/new" element={<MyLibraryAddPage />} />
                <Route path="my-library/add" element={<MyLibraryAddPage />} />
                <Route
                  path="my-library/add/book"
                  element={<MyLibraryAddBookPage />}
                />
                <Route
                  path="my-library/add/movie"
                  element={<MyLibraryAddMoviePage />}
                />
                <Route
                  path="my-library/add/series"
                  element={<MyLibraryAddSeriesSeasonPage />}
                />
                <Route
                  path="my-library/entries"
                  element={<MyLibraryEntriesPage />}
                />
                <Route
                  path="my-library/edit/:id"
                  element={<MyLibraryEditPage />}
                />
                <Route
                  path="my-library/stats"
                  element={<MyLibraryStatsPage />}
                />

                {/* Purchases */}
                <Route path="purchases" element={<PurchasesListPage />} />
                <Route path="purchases/new" element={<PurchaseFormPage />} />
                <Route
                  path="purchases/:id/edit"
                  element={<PurchaseFormPage />}
                />
                <Route path="watch-brands" element={<WatchBrandsPage />} />
                <Route path="perfume-brands" element={<PerfumeBrandsPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
